import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, statusTypesData } from "@/data";
import {
    createBulkVideoDistributionsMutation,
    createVideoDistributionMutation,
    getDistributionsListQuery,
} from "@/graph";
import { openAlert, toggleDistributionDrawer } from "@/redux/actions";
import { displayBulkDistributionResultsHandler, displayErrorHandler } from "@/utils";

const useCreateVideoDistribution = (
    releasesIds = [],
    hasBulkDistributions,
    hasRefetch,
    limit,
    page,
    setBulkResults,
) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const ids = releasesIds.filter(Boolean).map((releaseId) => parseInt(releaseId));

    const [createVideoDistributionAction, { loading }] = useMutation(
        hasBulkDistributions ? createBulkVideoDistributionsMutation : createVideoDistributionMutation,
        {
            ...(hasRefetch && {
                awaitRefetchQueries: true,
                refetchQueries: [
                    {
                        query: getDistributionsListQuery,
                        variables: {
                            limit,
                            page,
                            ...(ids[0] && { releaseId: ids[0] }),
                        },
                    },
                ],
            }),
        },
    );

    const createVideoDistribution = async ({ client, distributeTypeId, releaseDate, territory, videos }) => {
        try {
            if (hasBulkDistributions && ids.length > import.meta.env.VITE_RELEASES_DOWNLOAD_LIMIT) {
                dispatch(
                    openAlert(
                        t("messages.distributionMsgs.entitiesLengthError", { entity: t("releases.name") }),
                        statusTypesData.error,
                    ),
                );

                return;
            }

            await createVideoDistributionAction({
                onCompleted: ({ bulkDistribute }) => {
                    if (hasBulkDistributions) {
                        displayBulkDistributionResultsHandler(
                            bulkDistribute?.results,
                            t("messages.distributionMsgs.video.bulkSuccess"),
                            setBulkResults,
                            dispatch,
                            t,
                        );

                        return;
                    }

                    dispatch(openAlert(t("messages.distributionMsgs.video.success"), statusTypesData.success));

                    dispatch(toggleDistributionDrawer(false));
                },
                variables: {
                    clientId: client,
                    distributeTypeId,
                    releaseDate,
                    territoryId: territory,
                    // one job and one XML per video is spawned backend side; bulk sends no ids at all
                    // because every video of every selected release goes out
                    ...(hasBulkDistributions
                        ? { releasesIds: ids }
                        : {
                              releaseId: ids[0],
                              videosId: videos?.map(({ value }) => value) || [],
                          }),
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    return {
        createVideoDistribution,
        loading,
    };
};

export default useCreateVideoDistribution;
